import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import { IPartCategory, ISingleCategoryResponse } from "../models/Part";

export default function useCategorySingle(categoryId: string | undefined) {
  const { get, post, put, del } = useApiHelper();
  const queryClient = useQueryClient();

  const upsertCategoryInRow = (row: any, incomingCategory: Partial<IPartCategory>) => {
    if (!row || !incomingCategory?.id) return row;

    const rowCategoryId = row.categoryId || row.category?.id;
    if (rowCategoryId !== incomingCategory.id) {
      return row;
    }

    return {
      ...row,
      categoryId: incomingCategory.id,
      category: {
        ...(row.category || {}),
        ...incomingCategory,
      },
    };
  };

  const patchPagedTableCategory = (incomingCategory: Partial<IPartCategory>) => {
    const pagedQueries = queryClient.getQueriesData(["pagedTable"]);

    pagedQueries.forEach(([queryKey]) => {
      queryClient.setQueryData(queryKey, (currentData: any) => {
        if (!currentData?.data?.rows) return currentData;

        return {
          ...currentData,
          data: {
            ...currentData.data,
            rows: currentData.data.rows.map((row: any) => upsertCategoryInRow(row, incomingCategory)),
          },
        };
      });
    });
  };

  const category = useQuery({
    queryKey: ["categories", "single", categoryId],
    queryFn: () => get<ISingleCategoryResponse>(`/part/categories/${categoryId}/single`),
    enabled: !!categoryId,
  });

  const create = useMutation(
    (category: IPartCategory) => post(`/part/categories`, category),
    {
      onMutate: async (category: IPartCategory) => {
        await queryClient.cancelQueries(["pagedTable"]);
        const previousPagedQueries = queryClient.getQueriesData(["pagedTable"]);

        patchPagedTableCategory(category);

        return { previousPagedQueries };
      },
      onError: (_error, _category, context: any) => {
        context?.previousPagedQueries?.forEach(([queryKey, oldData]: any) => {
          queryClient.setQueryData(queryKey, oldData);
        });
      },
      onSuccess: (response) => {
        const savedCategory = response?.data?.data ?? response?.data;
        if (savedCategory) {
          patchPagedTableCategory(savedCategory);
        }

        queryClient.invalidateQueries(["categories"]);
        queryClient.invalidateQueries(["pagedTable"]);
      },
    }
  );

  const update = useMutation(
    (category: IPartCategory) => put(`/part/categories/${category.id}`, category),
    {
      onMutate: async (category: IPartCategory) => {
        await queryClient.cancelQueries(["pagedTable"]);
        const previousPagedQueries = queryClient.getQueriesData(["pagedTable"]);

        patchPagedTableCategory(category);

        return { previousPagedQueries };
      },
      onError: (_error, _category, context: any) => {
        context?.previousPagedQueries?.forEach(([queryKey, oldData]: any) => {
          queryClient.setQueryData(queryKey, oldData);
        });
      },
      onSuccess: (response) => {
        const savedCategory = response?.data?.data ?? response?.data;
        if (savedCategory) {
          patchPagedTableCategory(savedCategory);
        }

        queryClient.invalidateQueries(["categories"]);
        queryClient.invalidateQueries(["pagedTable"]);
      },
    }
  );

  const bulkUpdatePartCategories = useMutation(
    (body: any) => post(`/part/categories/bulk`, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["categories"]);
        queryClient.invalidateQueries(["categoryParts"]);
      },
    }
  );

  return {
    category,
    create,
    update,
    bulkUpdatePartCategories
  };
}
